import React from "react";
import {
  fetchTaktNodeMapTree,
  setTaktNodeMappingBulk,
} from "../../../../api/clientV2";
import type {
  SetTaktNodeMappingBody,
  TaktNodeMapNode,
  TaktNodeMapping,
} from "../../../../models";
import LeafRow from "./LeafRow/LeafRow";
import AddNodeToSection from "./AddNodeToSection/AddNodeToSection";
import { groupNodesBySection, type SectionGroup } from "./groupBySection";
import { naturalCompare } from "../../../../utils/sort";
import "./WbsTab.css";

interface Props {
  scheduleId: number;
  /** Called after a successful save so the host can refresh the section list
   *  (a new section created here must appear on the other tabs). */
  onSaved?: () => void;
}

type Dim = keyof TaktNodeMapping;
type Pending = Record<number, Partial<TaktNodeMapping>>;
type View = "sections" | "tree";

/** Edit Board WBS↔Takt mapping. Each node that holds tasks gets a Takt
 *  section/zone/level; edits STAGE locally and commit on "Save mappings". An
 *  in-tab toggle flips between the Takt-section grouping and the full WBS tree —
 *  both share this state. */
const WbsTab: React.FC<Props> = ({ scheduleId, onSaved }) => {
  const [view, setView] = React.useState<View>("sections");
  const [nodes, setNodes] = React.useState<TaktNodeMapNode[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [pending, setPending] = React.useState<Pending>({});
  const [collapsed, setCollapsed] = React.useState<Set<number>>(new Set());
  const [collapsedSections, setCollapsedSections] = React.useState<Set<string>>(new Set());
  // Sections created here this session (no nodes yet) — pickable in the row
  // dropdowns immediately; they persist once a node is assigned and saved.
  const [createdSections, setCreatedSections] = React.useState<string[]>([]);
  const [newName, setNewName] = React.useState("");

  const load = React.useCallback(() => {
    fetchTaktNodeMapTree(scheduleId)
      .then(setNodes)
      .catch(() => setError("Couldn't load the WBS structure."));
  }, [scheduleId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const committedById = React.useMemo(() => {
    const m = new Map<number, TaktNodeMapNode>();
    (nodes ?? []).forEach((n) => m.set(n.id, n));
    return m;
  }, [nodes]);

  const sectionGroups = React.useMemo(() => {
    const groups = groupNodesBySection(nodes ?? [], pending);
    // A freshly-created section has no nodes yet — show it as an empty group
    // pinned to the TOP of the list (newest first) so it's visible the moment
    // you add it. Once a node is filed into it, it joins the sorted groups.
    const present = new Set(groups.map((g) => g.section));
    const empties: SectionGroup[] = createdSections
      .filter((s) => s && !present.has(s))
      .map((s) => ({ section: s, nodeCount: 0, taskCount: 0, rows: [] }));
    return empties.length ? [...empties, ...groups] : groups;
  }, [nodes, pending, createdSections]);

  // Every task-holding node with its current Takt section — the candidate pool
  // for each section's "add WBS node" picker.
  const taskNodes = React.useMemo(
    () =>
      (nodes ?? [])
        .filter((n) => n.counts.tasks > 0)
        .map((n) => ({
          id: n.id,
          name: n.name,
          section: ((pending[n.id]?.section_name ?? n.mapping.section_name) || "").trim(),
        })),
    [nodes, pending],
  );

  const sectionOptions = React.useMemo(() => {
    const s = new Set<string>();
    (nodes ?? []).forEach((n) => {
      if (n.mapping.section_name) s.add(n.mapping.section_name);
    });
    Object.values(pending).forEach((p) => {
      if (p.section_name) s.add(p.section_name);
    });
    createdSections.forEach((n) => s.add(n));
    return Array.from(s).sort(naturalCompare);
  }, [nodes, pending, createdSections]);

  const childrenOf = React.useMemo(() => {
    const m = new Map<number | null, TaktNodeMapNode[]>();
    (nodes ?? []).forEach((n) => {
      const arr = m.get(n.parent_id) ?? [];
      arr.push(n);
      m.set(n.parent_id, arr);
    });
    return m;
  }, [nodes]);

  const branchIds = React.useMemo(
    () => (nodes ?? []).filter((n) => !n.is_leaf).map((n) => n.id),
    [nodes],
  );

  const toggleCollapse = (id: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const collapseAll = () => setCollapsed(new Set(branchIds));
  const expandAll = () => setCollapsed(new Set());

  const sectionKey = (section: string) => section || "__unmapped";
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const collapseAllSections = () =>
    setCollapsedSections(new Set(sectionGroups.map((g) => sectionKey(g.section))));
  const expandAllSections = () => setCollapsedSections(new Set());

  // Stage an edit; drop it from pending if it returns to the committed value.
  const onEdit = React.useCallback(
    (nodeId: number, dim: Dim, value: string) => {
      setPending((prev) => {
        const committed = committedById.get(nodeId)?.mapping[dim] ?? "";
        const node = { ...(prev[nodeId] ?? {}) };
        if (value === committed) {
          delete node[dim];
        } else {
          node[dim] = value;
        }
        const next = { ...prev };
        if (Object.keys(node).length === 0) {
          delete next[nodeId];
        } else {
          next[nodeId] = node;
        }
        return next;
      });
    },
    [committedById],
  );

  const pendingCount = Object.keys(pending).length;

  const onSave = async () => {
    if (pendingCount === 0) return;
    setBusy(true);
    setError(null);
    try {
      const edits: SetTaktNodeMappingBody[] = Object.entries(pending).map(
        ([nodeId, dims]) => ({ wbs_node_id: Number(nodeId), ...dims }),
      );
      await setTaktNodeMappingBulk(scheduleId, edits);
      setPending({});
      load();
      onSaved?.();
    } catch {
      setError("Save failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onDiscard = () => setPending({});

  // Create a brand-new Takt section with no nodes yet — it becomes pickable in
  // every row's Section dropdown immediately; assigning a node and saving
  // persists it.
  const createSection = () => {
    const v = newName.trim();
    setNewName("");
    if (!v || sectionOptions.includes(v)) return;
    setCreatedSections((prev) => (prev.includes(v) ? prev : [v, ...prev]));
  };

  const renderNode = (n: TaktNodeMapNode, depth: number): React.ReactNode => {
    const kids = childrenOf.get(n.id) ?? [];
    const hasChildren = kids.length > 0;
    // A node is mappable when it carries activities DIRECTLY — not just leaves.
    // P6 attaches tasks to branch nodes too (e.g. "Interior Finishes - BlkA.1 -
    // Level 3" has 32 of its own tasks plus child nodes); those tasks inherit
    // this node's mapping, so it must be editable here.
    const mappable = n.counts.tasks > 0;
    const isCollapsed = collapsed.has(n.id);

    // Pure leaf, or a branch that holds tasks of its own → an editable row.
    if (!hasChildren || mappable) {
      const row = (
        <LeafRow
          key={n.id}
          node={n}
          depth={depth}
          pending={pending[n.id]}
          onEdit={onEdit}
          disabled={busy}
          sectionOptions={sectionOptions}
          collapsible={hasChildren}
          collapsed={isCollapsed}
          onToggle={() => toggleCollapse(n.id)}
        />
      );
      if (!hasChildren) return row;
      return (
        <React.Fragment key={n.id}>
          {row}
          {!isCollapsed && kids.map((k) => renderNode(k, depth + 1))}
        </React.Fragment>
      );
    }

    // Pure branch (no direct tasks): a collapsible header only.
    return (
      <React.Fragment key={n.id}>
        <div
          className="wbsmap-branch"
          style={{ paddingLeft: depth * 18 + 12 }}
          role="button"
          tabIndex={0}
          onClick={() => toggleCollapse(n.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleCollapse(n.id);
            }
          }}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          <span className="wbsmap-chevron">{isCollapsed ? "▸" : "▾"}</span>
          <span className="wbsmap-branch-name" title={n.name}>
            {n.name}
          </span>
        </div>
        {!isCollapsed && kids.map((k) => renderNode(k, depth + 1))}
      </React.Fragment>
    );
  };

  // The "Sections" view: each Takt section is a flat list of its task-holding
  // nodes, every row showing the node's full WBS path (root excluded) as a
  // ">"-delimited breadcrumb. Section headers collapse.
  const renderSectionGroups = (): React.ReactNode =>
    sectionGroups.map((g) => {
      const key = sectionKey(g.section);
      const isCollapsed = collapsedSections.has(key);
      return (
        <div key={key} className="wbsmap-secgroup">
          <div
            className={`wbsmap-sechead ${g.section ? "" : "is-unmapped"}`}
            role="button"
            tabIndex={0}
            onClick={() => toggleSection(key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggleSection(key);
            }}
          >
            <span className="wbsmap-chevron">{isCollapsed ? "▸" : "▾"}</span>
            <span className="wbsmap-sechead-name">{g.section || "⚠ Not mapped"}</span>
            <span className="wbsmap-sechead-meta">
              {g.nodeCount} node{g.nodeCount === 1 ? "" : "s"} · {g.taskCount} tasks
            </span>
          </div>
          {!isCollapsed &&
            g.rows.map((r) => {
              const ancestors = r.path.slice(0, -1); // path leading to the node
              return (
                <div key={r.node.id} className="wbsmap-secrow">
                  {ancestors.length > 0 && (
                    <div className="wbsmap-secrow-path" title={ancestors.join(" › ")}>
                      {ancestors.map((seg, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="wbsmap-path-sep"> › </span>}
                          {seg}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  <LeafRow
                    node={r.node}
                    depth={1}
                    pending={pending[r.node.id]}
                    onEdit={onEdit}
                    disabled={busy}
                    sectionOptions={sectionOptions}
                  />
                </div>
              );
            })}
          {!isCollapsed && g.section && (
            <AddNodeToSection
              candidates={taskNodes.filter((c) => c.section !== g.section)}
              disabled={busy}
              onPick={(nodeId) => onEdit(nodeId, "section_name", g.section)}
            />
          )}
        </div>
      );
    });

  if (nodes === null) {
    return <div className="wbsmap-hint">Loading WBS…</div>;
  }

  const roots = childrenOf.get(null) ?? [];

  return (
    <div className="wbsmap">
      <div className="wbsmap-bar">
        <div className="wbsmap-seg" role="tablist">
          <button
            type="button"
            className={`wbsmap-segbtn ${view === "sections" ? "is-on" : ""}`}
            onClick={() => setView("sections")}
          >
            Sections
          </button>
          <button
            type="button"
            className={`wbsmap-segbtn ${view === "tree" ? "is-on" : ""}`}
            onClick={() => setView("tree")}
          >
            WBS
          </button>
        </div>
        <div className="wbsmap-bar-hint">
          {view === "sections" ? (
            <>
              Each Takt <b>section</b> lists its WBS nodes by full path. Set or fix a
              node's <b>zone · level</b> on its row; it re-files live.
            </>
          ) : (
            <>
              Set the Takt <b>section · zone · level</b> on any WBS node that holds
              tasks (a branch with its own tasks is editable too).
            </>
          )}
        </div>
        <button
          type="button"
          className="wbsmap-treebtn"
          onClick={view === "sections" ? collapseAllSections : collapseAll}
        >
          Collapse all
        </button>
        <button
          type="button"
          className="wbsmap-treebtn"
          onClick={view === "sections" ? expandAllSections : expandAll}
        >
          Expand all
        </button>
      </div>
      <div className="wbsmap-newsec">
        <input
          className="wbsmap-newsec-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              createSection();
            }
          }}
          placeholder="New Takt section name…"
          disabled={busy}
        />
        <button
          type="button"
          className="wbsmap-newsec-btn"
          onClick={createSection}
          disabled={busy || !newName.trim()}
        >
          ＋ Create section
        </button>
      </div>
      {error && <div className="wbsmap-error">{error}</div>}
      <div className="wbsmap-tree">
        <div className="wbsmap-head">
          <span className="wbsmap-head-name">{view === "sections" ? "Takt structure" : "WBS node"}</span>
          <span className="wbsmap-head-cell">Section</span>
          <span className="wbsmap-head-cell">A</span>
          <span className="wbsmap-head-cell">Zone (B)</span>
          <span className="wbsmap-head-cell">Level (C)</span>
          <span className="wbsmap-head-counts">On board</span>
        </div>
        {view === "sections" ? renderSectionGroups() : roots.map((r) => renderNode(r, 0))}
      </div>
      <div className="wbsmap-foot">
        <span className="wbsmap-foot-note">
          {pendingCount > 0
            ? `${pendingCount} node${pendingCount === 1 ? "" : "s"} edited — not saved yet`
            : "All mappings saved."}
        </span>
        <button
          type="button"
          className="wbsmap-discard"
          onClick={onDiscard}
          disabled={pendingCount === 0 || busy}
        >
          Discard
        </button>
        <button
          type="button"
          className="wbsmap-save"
          onClick={onSave}
          disabled={pendingCount === 0 || busy}
        >
          {busy ? "Saving…" : "Save mappings"}
        </button>
      </div>
    </div>
  );
};

export default WbsTab;
